import Phaser from "phaser";
import { preloadPhaserAssets } from "../../sprite-loader";
import {
  registerInventoryAndPlayerListeners,
  unregisterInventoryAndPlayerListeners,
  registerDisconnectHandler,
  unregisterDisconnectHandler,
} from "../../network";
import { WindowManager } from "../../ui/window-manager";
import { SquadWindow } from "./windows/squad-window";
import { SettingsWindow } from "./windows/settings-window";
import { EndScreen } from "../../ui/end-screen";
import { DailyRewardScreen } from "../../ui/daily-reward-screen";
import { NotificationService } from "../../services/notification-service";
import { MatchEndData } from "../../types/match-end-data";
import { PlayerData, LIMBS_DEFAULT_VALUES, SquadMemberDetail, SquadUpdateData } from "../../models/common";
import { trackEvent } from "../../utils/ga-events";
import { BaseNav, TAB_ORDER } from "./base-nav";
import { getSafeAreaInsetLeft } from "../../utils/mobile-utils";
import { RaiderScreen } from "./screens/raider-screen";
import { QuestsScreen } from "./screens/quests-screen";
import { StorageScreen } from "./screens/storage-screen";
import { CraftScreen } from "./screens/craft-screen";
import { EmptyScreen } from "./screens/empty-screen";
import { DeployScreen } from "./screens/deploy-screen";
import { SeasonScreen } from "./screens/season-screen";
import { GauntletScreen, hasActiveGauntletRun } from "./screens/gauntlet-screen";
import { MatchEndReason } from "../../types/match-end-data";

export class BaseScene extends Phaser.Scene {
  private uiLayer!: Phaser.GameObjects.Container;
  private windowManager!: WindowManager;

  private endScreen: EndScreen | undefined;
  private dailyRewardScreen: DailyRewardScreen | undefined;

  private carouselContainer!: Phaser.GameObjects.Container;
  private nav!: BaseNav;
  private raiderScreen!: RaiderScreen;
  private questsScreen!: QuestsScreen;
  private storageScreen!: StorageScreen;
  private craftScreen!: CraftScreen;
  private squadWindow!: SquadWindow;
  private squadSideButton: Phaser.GameObjects.Image | null = null;
  private settingsWindow!: SettingsWindow;
  private seasonScreen!: SeasonScreen;
  private seasonOverlayContainer!: Phaser.GameObjects.Container;
  private preSeasonTabIndex: number = 0;
  private seasonScreenOpen: boolean = false;
  private gauntletScreen!: GauntletScreen;
  private gauntletOverlayContainer!: Phaser.GameObjects.Container;
  private preGauntletTabIndex: number = 0;
  // Set when the season screen is opened from the post-match reveal chain (see
  // EndScreen.setOnHideCallback) — invoked once the player closes it so the chain
  // can continue (e.g. restoring the daily-reward/tutorial flow).
  private postMatchSeasonCompleteFn: (() => void) | null = null;
  private emptyScreen!: EmptyScreen;
  private deployScreen!: DeployScreen;
  // Set right before emitting squad-join; consumed the next time squad-info
  // arrives with a full (2-member) squad, so only the joiner auto-navigates
  // to the deploy screen - the already-present member is left where they are.
  private pendingSquadJoinNavigate: boolean = false;
  private pendingRaiderEquipmentRefresh: boolean = false;
  private sideButtonContainer: Phaser.GameObjects.Container | null = null;
  private bgImage: Phaser.GameObjects.Image | null = null;
  private readonly PARALLAX_FACTOR = 0.1;
  private seasonPassListener: ((data: PlayerData) => void) | null = null;
  baseFireSound: Phaser.Sound.BaseSound | null = null;
  private baseMusic: Phaser.Sound.BaseSound | null = null;

  private currentTabIndex: number = 0;

  // Drag / swipe state
  private pointerDown: boolean = false;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private carouselBaseX: number = 0;

  constructor() {
    super("BaseScene");
    trackEvent("enter-base");
  }

  preload(): void {
    preloadPhaserAssets(this);

    // Background image — fails silently if not yet present
    if (!this.textures.exists("base-bg")) {
      this.load.image("base-bg", "/sprites/base/bg-long.png");
    }


    WindowManager.preload(this);
    DailyRewardScreen.preload(this);
    GauntletScreen.preload(this);
  }

  create(
    data: {
      matchEndData?: MatchEndData;
      playerData?: PlayerData;
      showLoadingOverlay?: boolean;
      onSceneReady?: () => void;
      squadBlockingMessage?: string;
    } = {}
  ): void {
    this.events.once("shutdown", this.shutdown, this);

    // Storage window refresh is now handled in the global player-data handler in network.ts
    // Handle reconnection results stored on window
    if ((window as any).pendingMatchResults) {
      const pending = (window as any).pendingMatchResults;
      delete (window as any).pendingMatchResults;
      data.matchEndData = pending.matchEndData;
      data.playerData = pending.playerData;
    }

    // When returning from a match, hydrate global player data immediately so
    // RaiderScreen can render the correct equipped weapon before socket refresh arrives.
    if (data.playerData) {
      if (!(window as any).player) {
        (window as any).player = { data: {} } as any;
      }
      (window as any).player.data = {
        ...(window as any).player.data,
        ...data.playerData,
      };
    }

    // Always reset limbs to healthy on base entry — bleeding is match-only state.
    if ((window as any).player?.data) {
      (window as any).player.data.limbs = { ...LIMBS_DEFAULT_VALUES };
    }

    const { width, height } = this.scale;

    // Fade-in support: hide main camera until ready if loading overlay is shown
    this.cameras.main.setAlpha(data.showLoadingOverlay ? 0 : 1);

    // ── Background ──────────────────────────────────────────────────────────
    const bgFill = this.add.graphics();
    bgFill.fillStyle(0x1a2a3a, 1);
    bgFill.fillRect(0, 0, width, height);
    bgFill.setDepth(0);

    const hasPremiumSeasonPass = !!(window as any).player?.data?.seasonPassOwned;
    console.log("[BaseScene] seasonPassOwned:", hasPremiumSeasonPass);
    const bgKey = hasPremiumSeasonPass && this.textures.exists("season-bg") ? "season-bg" : "base-bg";

    if (this.textures.exists(bgKey)) {
      const src = this.textures.get(bgKey).getSourceImage() as HTMLImageElement;
      const displayH = height;
      const displayW = src.width && src.height ? (src.width / src.height) * displayH : width;
      this.bgImage = this.add
        .image(width / 2, height / 2, bgKey)
        .setDisplaySize(displayW, displayH)
        .setDepth(1);
    }

    // Live-update the bg (and nav's season button, see below) when the player's premium
    // status changes mid-session — e.g. via the season screen's debug ACTIVATE button —
    // rather than only reflecting it the next time BaseScene is entered.
    this.seasonPassListener = (playerData: PlayerData) => {
      this.updateBackgroundForSeasonPass(!!playerData?.seasonPassOwned);
      this.nav?.setSeasonPremium(!!playerData?.seasonPassOwned);
      this.nav?.setSeasonLevel(playerData?.seasonXp ?? 0);
    };
    window.socket?.on("player-data", this.seasonPassListener);

    // ── Base ambience ────────────────────────────────────────────────────────
    // When returning from a match, the end screen (death/timeout/extraction) is
    // about to be shown — defer base ambience until it's dismissed, so it never
    // plays underneath the death screen. Otherwise (normal base entry) start now.
    if (!(data.matchEndData && data.playerData)) {
      this.startBaseAmbience();
    }

    // ── UI Layer ─────────────────────────────────────────────────────────────
    this.uiLayer = this.add.container(0, 0);
    this.uiLayer.setDepth(100000);

    // ── WindowManager ────────────────────────────────────────────────────────
    if ((window as any).windowManager) {
      (window as any).windowManager.destroy?.();
    }
    this.windowManager = new WindowManager(this, this.uiLayer);
    (window as any).windowManager = this.windowManager;

    // ── Socket listeners ─────────────────────────────────────────────────────
    registerInventoryAndPlayerListeners(this, this.windowManager);
    registerDisconnectHandler(this.game);

    if (window.socket) {
      window.socket.emit("request-player-data");
      window.socket.emit("player-enter-base");
      window.socket.emit("squad-check");
    }

    // ── Squad blocking ───────────────────────────────────────────────────────
    if (data.squadBlockingMessage) {
      this.windowManager.showNotification(data.squadBlockingMessage, "warning");
    }
    if (window.socket) {
      window.socket.on("notification", (msg: { message: string; type: string }) => {
        this.windowManager?.showNotification(msg.message, (msg.type as any) ?? "info");
      });
    }

    // ── Daily reward screen ──────────────────────────────────────────────────
    this.dailyRewardScreen = new DailyRewardScreen(this);
    this.dailyRewardScreen.setWindowManager(this.windowManager);

    // ── Match end screen (if returning from extraction) ───────────────────────
    if (data.matchEndData && data.playerData) {
      this.windowManager.hideAll();
      this.endScreen = new EndScreen(this);

      this.endScreen.setOnHideCallback((completeFn: () => void) => {
        this.windowManager.showUIButtons(this);
        this.createSideButtons();

        const matchEndData = data.matchEndData!;
        const pvpGained = (matchEndData.matchEvents || []).reduce((sum, e) => sum + e.amount, 0);
        const xpGained = (matchEndData.seasonXpEvents || []).reduce((sum, e) => sum + e.amount, 0);

        // Only open the season screen (with its animated xp-gain bar and any
        // level-up reward reveals) if the player actually gained season xp.
        const proceedToSeason = () => {
          if (xpGained > 0) {
            this.postMatchSeasonCompleteFn = completeFn;
            this.openSeasonScreen({ animateFromXp: matchEndData.seasonXp });
          } else {
            completeFn();
          }
        };

        // Only show the leaderboard if the player actually gained pvp points.
        const showLeaderboardStep = (next: () => void) => {
          if (pvpGained <= 0) {
            next();
            return;
          }
          // Keep the empty screen visible behind the leaderboard overlay.
          this.scrollToTab(5, false);
          this.nav.setActiveTab(null as any);
          this.nav.hide();

          this.windowManager.showRanksWindow(() => {
            this.windowManager.hideRanksWindow();
            next();
          });
        };

        // If the extraction just completed today's daily challenge day, drop the
        // player on the quests screen to see the confetti/bounce on its widget before
        // moving on to whatever else is queued up (leaderboard, season screen, ...).
        const dailyChallengeReward = matchEndData.dailyChallenge;
        const showDailyChallengeStep = (next: () => void) => {
          if (!dailyChallengeReward) {
            next();
            return;
          }
          this.windowManager.showNotification(
            `Day ${dailyChallengeReward.day} complete! +${dailyChallengeReward.creditsAwarded} Credits`,
            "success",
            "DAILY CHALLENGE",
          );
          NotificationService.getInstance().scheduleNextDayReminder();
          this.questsScreen.celebrateDailyChallengeCompletion();
          this.scrollToTab(TAB_ORDER.indexOf("quests"), false);
          // Give the player a moment to actually see the confetti/bounce before the
          // next queued step (if any) navigates away from this screen.
          this.time.delayedCall(1500, next);
        };

        // A gauntlet run advances on every successful extraction and is wiped out by
        // the next failed one (see gauntlet-screen.ts) - only show the overlay when
        // there's something to reveal (a success, or a run that was active when the
        // player died/timed out).
        const gauntletSucceeded = matchEndData.reason === MatchEndReason.EXTRACTION_SUCCESS;
        const showGauntletStep = (next: () => void) => {
          if (!gauntletSucceeded && !hasActiveGauntletRun()) {
            next();
            return;
          }
          this.scrollToTab(TAB_ORDER.length, false);
          this.nav.setActiveTab(null as any);
          this.nav.hide();
          this.sideButtonContainer?.setVisible(false);
          this.uiLayer.bringToTop(this.gauntletOverlayContainer);

          const finish = () => {
            this.nav.show();
            this.sideButtonContainer?.setVisible(true);
            next();
          };

          if (gauntletSucceeded) {
            this.gauntletScreen.showExtractionSuccess(finish);
          } else {
            this.gauntletScreen.showExtractionFailure(finish);
          }
        };

        showGauntletStep(() => {
          showDailyChallengeStep(() => {
            showLeaderboardStep(() => {
              this.scrollToTab(0, false);
              proceedToSeason();
            });
          });
        });
      });

      this.endScreen.show(data.matchEndData, data.playerData);
    }

    // ── Carousel panels ──────────────────────────────────────────────────────
    // Carousel sits in its own container below the UI layer so that UI elements
    // (top buttons, nav bar) always render on top.
    this.carouselContainer = this.add.container(0, 0);
    this.carouselContainer.setDepth(99999);

    const raiderPanel = this.add.container(0, 0);
    const workshopPanel = this.add.container(width, 0);
    const storagePanel = this.add.container(width * 2, 0);
    const questsPanel = this.add.container(width * 3, 0);
    const deployPanel = this.add.container(width * 4, 0);
    const emptyPanel = this.add.container(width * 5, 0);
    this.carouselContainer.add([raiderPanel, workshopPanel, storagePanel, questsPanel, deployPanel, emptyPanel]);

    // Season is a fixed full-screen overlay, not a carousel panel — it's parented to
    // uiLayer so it never moves with carouselContainer's drag/scroll (see openSeasonScreen).
    this.seasonOverlayContainer = this.add.container(0, 0);
    this.seasonOverlayContainer.setVisible(false);
    this.uiLayer.add(this.seasonOverlayContainer);

    // Gauntlet is also a fixed full-screen overlay, parented to uiLayer for the same
    // reason as the season overlay above.
    this.gauntletOverlayContainer = this.add.container(0, 0);
    this.gauntletOverlayContainer.setVisible(false);
    this.uiLayer.add(this.gauntletOverlayContainer);

    // ── Base-scene windows ──────────────────────────────────────────────────────
    this.squadWindow = new SquadWindow(this, this.uiLayer, () => {
      this.pendingSquadJoinNavigate = true;
    });
    this.settingsWindow = new SettingsWindow(this, this.windowManager, this.uiLayer);

    this.raiderScreen = new RaiderScreen(this, this.windowManager, raiderPanel, this.uiLayer);
    this.craftScreen = new CraftScreen(this, this.windowManager, workshopPanel);
    this.storageScreen = new StorageScreen(this, this.windowManager, storagePanel);
    this.questsScreen = new QuestsScreen(this, questsPanel, () => this.openGauntletScreen());
    this.seasonScreen = new SeasonScreen(this, this.seasonOverlayContainer, () => this.closeSeasonScreen());
    this.gauntletScreen = new GauntletScreen(this, this.gauntletOverlayContainer, this.windowManager);
    this.emptyScreen = new EmptyScreen(this, emptyPanel);
    this.deployScreen = new DeployScreen(
      this,
      this.windowManager,
      deployPanel,
      this.uiLayer,
      () => this.scrollToTab(0),
      () => this.goToLobby(),
    );

    this.events.on("squad-button-clicked", () => {
      if (this.squadWindow.isVisible()) {
        this.squadWindow.hide();
      } else {
        this.squadWindow.show();
      }
    });

    this.events.on("squad-info", (data: { squadCode: string; members: string[]; memberDetails?: SquadMemberDetail[] }) => {
      this.squadWindow.storeSquadCode(data.squadCode, data.members);
      this.updateSquadSideButton(true);

      const memberDetails = data.memberDetails || [];
      // window.player is often just a { data: {} } placeholder in the base scene
      // (only main-scene/market-scene attach a real Player with accountId) -
      // window.accountId is set once at login and is reliable everywhere.
      const myAccountId = (window as any).accountId;
      this.deployScreen.updateSquadState({
        squadCode: data.squadCode,
        memberDetails,
        myAccountId,
      });
      this.raiderScreen.updateSquadState(memberDetails, myAccountId);

      if (this.pendingSquadJoinNavigate && memberDetails.length === 2) {
        this.pendingSquadJoinNavigate = false;
        this.scrollToTab(TAB_ORDER.indexOf("deploy"));
      }
    });
    this.events.on("squad-left", () => {
      this.squadWindow.clearAndRefresh();
      this.updateSquadSideButton(false);
      this.pendingSquadJoinNavigate = false;
      this.deployScreen.clearSquadState();
      this.raiderScreen.clearSquadState();
    });
    this.events.on("squad-error", (data: { message: string }) => {
      this.squadWindow.showSquadErrorPopup(data.message);
    });
    this.events.on("squad-update", (data: SquadUpdateData) => {
      this.deployScreen.updateSquadMemberData(data);
      this.raiderScreen.updateSquadMemberData(data);
    });

    window.socket?.on("squad-join-lobby", () => this.goToLobby());

    this.events.on("raider-edit-name-requires-account", () => {
      this.openSettingsWindow(true);
    });

    this.events.on("raider-open-cosmetics", () => {
      this.openCosmeticsWindow();
    });

    this.events.on("season-open-cosmetics", () => {
      this.openCosmeticsWindow();
    });

    // ── Nav bar ──────────────────────────────────────────────────────────────
    this.nav = new BaseNav(
      this,
      this.uiLayer,
      (tab) => {
        if (tab === "season") {
          this.openSeasonScreen();
          return;
        }
        this.scrollToTab(TAB_ORDER.indexOf(tab));
      },
      () => this.enterLobby(),
    );

    this.events.on("raider-market-clicked", () => this.enterMarketplace());

    // ── Drag / swipe handling ────────────────────────────────────────────────
    this.setupCarouselDrag();

    // ── Deferred UI init ─────────────────────────────────────────────────────
    // Small delay matches old scene pattern — ensures assets have rendered
    this.time.delayedCall(100, () => {
      const endScreenVisible = this.endScreen?.isVisible?.();
      const rewardVisible = this.dailyRewardScreen?.isVisible?.();

      if (!endScreenVisible && !rewardVisible) {
        this.windowManager.showUIButtons(this);
        this.createSideButtons();
        this.scrollToTab(0, false);
        if (this.pendingRaiderEquipmentRefresh) {
          this.pendingRaiderEquipmentRefresh = false;
          this.refreshRaiderEquipment();
        }
      }

      if (data.onSceneReady) {
        data.onSceneReady();
      }
    });

  }

  private createSideButtons(): void {
    if (this.sideButtonContainer) {
      this.squadSideButton = null;
      this.sideButtonContainer.destroy();
      this.sideButtonContainer = null;
    }

    const { height } = this.scale;
    const btnSize = 48;
    const spacing = 12;
    const numButtons = 3;
    const totalHeight = numButtons * btnSize + (numButtons - 1) * spacing;
    const marginX = 16;
    const safeLeft = getSafeAreaInsetLeft();
    const x = marginX + safeLeft + btnSize / 2;
    // Center the group vertically, offset slightly above center
    const startY = (height - totalHeight) / 2 - 20;

    const buttonDefs = [
      {
        icon: "squad-icon",
        onClick: () => {
          if (this.squadWindow.isVisible()) {
            this.squadWindow.hide();
            this.scrollToTab(0);
          } else {
            this.closeSidePanelWindows("squad");
            this.raiderScreen.hide();
            this.scrollToTab(5, false);
            this.nav.setActiveTab(null as any);
            this.nav.hide();
            this.squadWindow.show(() => {
              this.squadWindow.hide();
              this.scrollToTab(0);
            });
          }
        },
      },
      {
        icon: "ranks-icon",
        onClick: () => {
          if (this.windowManager.isRanksWindowVisible()) {
            this.windowManager.hideRanksWindow();
            this.scrollToTab(0);
          } else {
            this.closeSidePanelWindows("ranks");
            this.raiderScreen.hide();
            this.scrollToTab(5, false);
            this.nav.setActiveTab(null as any);
            this.nav.hide();
            this.windowManager.showRanksWindow(() => {
              this.windowManager.hideRanksWindow();
              this.scrollToTab(0);
            });
          }
        },
      },
      {
        icon: "settings-icon",
        onClick: () => {
          if (this.settingsWindow.isVisible()) {
            this.settingsWindow.hide();
            this.scrollToTab(0);
          } else {
            this.closeSidePanelWindows("settings");
            this.openSettingsWindow(false);
          }
        },
      },
    ];

    const buttons: Phaser.GameObjects.Image[] = buttonDefs.map((def, i) => {
      const btnY = startY + i * (btnSize + spacing) + btnSize / 2;
      const btn = this.add
        .image(x, btnY, def.icon)
        .setDisplaySize(btnSize, btnSize)
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(100002)
        .setInteractive({ useHandCursor: true });
      btn.on("pointerdown", def.onClick);
      btn.once("destroy", () => {
        if (this.squadSideButton === btn) {
          this.squadSideButton = null;
        }
      });
      return btn;
    });

    this.squadSideButton = buttons[0] ?? null;

    this.sideButtonContainer = this.add.container(0, 0, buttons);
    this.sideButtonContainer.setScrollFactor(0);
    this.sideButtonContainer.setDepth(100002);
    this.uiLayer.add(this.sideButtonContainer);

    // Buttons are recreated with their default icons above - reapply the current
    // squad state immediately rather than waiting on the async squad-check
    // round trip (which may resolve well after this runs, or not at all if this
    // is a post-match recreation with no fresh squad-check in flight).
    this.updateSquadSideButton(!!this.squadWindow.getSquadCode());
  }

  private updateSquadSideButton(isActive: boolean): void {
    const button = this.squadSideButton;
    if (!button || !this.sys || (this.sys as any).isDestroyed) {
      return;
    }

    const buttonScene = (button as any).scene as Phaser.Scene | undefined;
    if (
      !button.active ||
      !buttonScene ||
      buttonScene !== this ||
      !buttonScene.sys ||
      (buttonScene.sys as any).isDestroyed
    ) {
      this.squadSideButton = null;
      return;
    }

    const iconKey = isActive ? "squad-active-icon" : "squad-icon";
    if (!this.textures.exists(iconKey)) {
      return;
    }

    if (button.texture?.key === iconKey) {
      return;
    }

    try {
      button.setTexture(iconKey);
    } catch {
      this.squadSideButton = null;
    }
  }

  private openSettingsWindow(showCreateAccountInput: boolean): void {
    this.raiderScreen.hide();
    // Instantly switch to the empty screen (index 5, not part of swipe navigation)
    this.scrollToTab(5, false);
    // Deselect all nav tab buttons and hide the nav bar
    this.nav.setActiveTab(null as any);
    this.nav.hide();
    // Show settings window (empty screen is now the only visible screen)
    this.settingsWindow.show(() => {
      this.settingsWindow.hide();
      this.scrollToTab(0);
    });

    if (showCreateAccountInput) {
      this.settingsWindow.showCreateAccountEmailInputWindow();
    }
  }

  private openCosmeticsWindow(): void {
    this.raiderScreen.hide();
    this.scrollToTab(5, false);
    this.nav.setActiveTab(null as any);
    this.nav.hide();
    this.raiderScreen.showCosmeticsWindow(() => {
      this.scrollToTab(0);
    });
  }

  private closeSidePanelWindows(except?: "squad" | "ranks" | "settings"): void {
    if (except !== "squad" && this.squadWindow.isVisible()) {
      this.squadWindow.hide();
    }

    if (except !== "ranks" && this.windowManager.isRanksWindowVisible()) {
      this.windowManager.hideRanksWindow();
    }

    if (except !== "settings" && this.settingsWindow.isVisible()) {
      this.settingsWindow.hide();
    }
  }

  private setupCarouselDrag(): void {
    const { width } = this.scale;
    const maxIndex = TAB_ORDER.length - 1;

    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Block carousel drag while any full-screen overlay is active — otherwise
      // a tap on the overlay's button captures carouselBaseX at the wrong position
      // and the subsequent drag snap can dismiss the overlay and jump to tab 0.
      if (this.endScreen?.isVisible()) return;
      if (this.windowManager.isRanksWindowVisible()) return;
      if (this.squadWindow?.isVisible()) return;
      if (this.seasonScreenOpen) return;
      if (this.gauntletScreen?.isVisible()) return;
      this.pointerDown = true;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.isDragging = false;
      this.carouselBaseX = this.carouselContainer.x;
    });

    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerDown) return;
      const dx = pointer.x - this.dragStartX;
      const dy = pointer.y - this.dragStartY;

      // Activate drag only when horizontal intent is clear
      if (
        !this.isDragging &&
        Math.abs(dx) > 12 &&
        Math.abs(dx) > Math.abs(dy) * 1.5
      ) {
        this.isDragging = true;
        // Recalculate base from the actual current carousel position — guards
        // against stale carouselBaseX if anything moved the carousel after pointerdown.
        this.carouselBaseX = this.carouselContainer.x - dx;
        // Dismiss any action-button overlays so they don't interfere
        this.windowManager.hideAll();
        this.showScreenForIndex(this.currentTabIndex);
      }

      if (this.isDragging) {
        const raw = this.carouselBaseX + dx;
        // Soft clamp past edges (rubber-band feel)
        const minX = -width * maxIndex;
        const clampedX = Phaser.Math.Clamp(raw, minX - 40, 40);
        this.carouselContainer.x = clampedX;
        this.updateBgParallax(clampedX);
      }
    });

    this.input.on("pointerup", () => {
      this.pointerDown = false;
      if (!this.isDragging) return;
      this.isDragging = false;

      const { width: w } = this.scale;
      const rawIndex = -this.carouselContainer.x / w;
      const dragFraction = rawIndex - this.currentTabIndex;
      const SWIPE_THRESHOLD = 0.2;

      let snapIndex: number;
      if (dragFraction > SWIPE_THRESHOLD) {
        snapIndex = this.currentTabIndex + 1;
      } else if (dragFraction < -SWIPE_THRESHOLD) {
        snapIndex = this.currentTabIndex - 1;
      } else {
        snapIndex = this.currentTabIndex;
      }

      this.scrollToTab(Phaser.Math.Clamp(snapIndex, 0, maxIndex));
    });
  }

  private updateBackgroundForSeasonPass(hasPremiumSeasonPass: boolean): void {
    console.log("[BaseScene] seasonPassOwned changed:", hasPremiumSeasonPass);
    const bgKey = hasPremiumSeasonPass && this.textures.exists("season-bg") ? "season-bg" : "base-bg";
    if (!this.bgImage || !this.textures.exists(bgKey)) return;

    const { height, width } = this.scale;
    const src = this.textures.get(bgKey).getSourceImage() as HTMLImageElement;
    const displayH = height;
    const displayW = src.width && src.height ? (src.width / src.height) * displayH : width;
    this.bgImage.setTexture(bgKey).setDisplaySize(displayW, displayH);
  }

  private updateBgParallax(carouselX: number): void {
    if (!this.bgImage) return;
    const { width } = this.scale;
    this.bgImage.x = width / 2 + carouselX * this.PARALLAX_FACTOR;
  }

  private scrollToTab(index: number, animate: boolean = true): void {
    const { width } = this.scale;
    const targetX = -index * width;
    this.currentTabIndex = index;
    this.nav.setActiveTab(TAB_ORDER[index]);
    this.showScreenForIndex(index);

    if (animate) {
      this.tweens.killTweensOf(this.carouselContainer);
      this.tweens.add({
        targets: this.carouselContainer,
        x: targetX,
        duration: 300,
        ease: "Power2",
        onUpdate: () => this.updateBgParallax(this.carouselContainer.x),
      });
    } else {
      this.carouselContainer.x = targetX;
      this.updateBgParallax(targetX);
    }
  }

  private showScreenForIndex(index: number): void {
    this.raiderScreen.hide();
    this.craftScreen.hide();
    this.storageScreen.hide();
    this.questsScreen.hide();
    this.seasonScreen.hide();
    this.emptyScreen.hide();
    this.deployScreen.hide();

    if (index >= 4) {
      this.nav.hide();
      this.sideButtonContainer?.setVisible(false);
    } else {
      this.nav.show();
      this.sideButtonContainer?.setVisible(true);
    }

    if (index === 0) this.raiderScreen.show();
    else if (index === 1) this.craftScreen.show();
    else if (index === 2) this.storageScreen.show();
    else if (index === 3) this.questsScreen.show();
    else if (index === 4) this.deployScreen.show();
    else if (index === 5) this.emptyScreen.show();
  }

  // Season is a fixed overlay (see seasonOverlayContainer) rather than a carousel panel —
  // scroll the carousel to the blank 'empty' tab behind it, then show the overlay on top.
  private openSeasonScreen(opts?: { animateFromXp?: number }): void {
    this.preSeasonTabIndex = this.currentTabIndex;
    this.seasonScreenOpen = true;
    this.scrollToTab(TAB_ORDER.length, false);
    this.uiLayer.bringToTop(this.seasonOverlayContainer);
    this.seasonScreen.show(opts?.animateFromXp);
  }

  private closeSeasonScreen(): void {
    this.seasonScreenOpen = false;
    this.seasonScreen.hide();
    this.scrollToTab(this.preSeasonTabIndex, false);

    const doneFn = this.postMatchSeasonCompleteFn;
    this.postMatchSeasonCompleteFn = null;
    doneFn?.();
  }

  // Gauntlet is opened on demand (e.g. from the quests-screen widget) to check the
  // current run's progress — same fixed-overlay pattern as the season screen above.
  private openGauntletScreen(): void {
    this.preGauntletTabIndex = this.currentTabIndex;
    this.scrollToTab(TAB_ORDER.length, false);
    this.uiLayer.bringToTop(this.gauntletOverlayContainer);
    this.gauntletScreen.show(() => this.closeGauntletScreen());
  }

  private closeGauntletScreen(): void {
    this.gauntletScreen.hide();
    this.scrollToTab(this.preGauntletTabIndex, false);
  }

  public refreshRaiderEquipment(): void {
    if (!this.raiderScreen) {
      this.pendingRaiderEquipmentRefresh = true;
      return;
    }

    this.raiderScreen.show();
  }

  private enterLobby(): void {
    this.scrollToTab(TAB_ORDER.indexOf("deploy"));
  }

  private goToLobby(): void {
    this.scene.stop("BaseScene");
    this.scene.start("LobbyScene");
  }

  private enterMarketplace(): void {
    const playerName = (window as any).player?.data?.name || "Player";
    const accountId = (window as any).player?.accountId;
    this.registry.set("playerState", { name: playerName, accountId });
    this.scene.stop("BaseScene");
    this.scene.start("MarketScene");
  }

  startBaseAmbience(): void {
    if (localStorage.getItem("sfxEnabled") !== "false" && !this.baseFireSound && this.cache.audio.exists("base-fire")) {
      this.baseFireSound = this.sound.add("base-fire", { volume: 0.3, loop: true });
      this.baseFireSound.play();
    }

    if (localStorage.getItem("musicEnabled") !== "false" && !this.baseMusic) {
      const baseTracks = ["base-1", "base-2"].filter((k) => this.cache.audio.exists(k));
      if (baseTracks.length > 0) {
        const key = baseTracks[Math.floor(Math.random() * baseTracks.length)];
        this.baseMusic = this.sound.add(key, { volume: 0.1, loop: true });
        this.baseMusic.play();
      }
    }
  }

  setMusicEnabled(enabled: boolean): void {
    if (enabled) {
      if (this.baseMusic) {
        this.baseMusic.resume();
      } else {
        this.startBaseAmbience();
      }
    } else if (this.baseMusic) {
      this.baseMusic.pause();
    }
  }

  shutdown(): void {
    this.squadSideButton = null;
    this.raiderScreen?.destroy();
    this.deployScreen?.destroy();
    this.seasonScreen?.destroy();
    this.gauntletScreen?.destroy();
    if (this.baseFireSound) {
      this.baseFireSound.stop();
      this.baseFireSound.destroy();
      this.baseFireSound = null;
    }
    if (this.baseMusic) {
      this.baseMusic.stop();
      this.baseMusic.destroy();
      this.baseMusic = null;
    }
    unregisterInventoryAndPlayerListeners();
    unregisterDisconnectHandler();
    if (window.socket) {
      window.socket.off("notification");
      if (this.seasonPassListener) {
        window.socket.off("player-data", this.seasonPassListener);
        this.seasonPassListener = null;
      }
    }
    this.windowManager?.destroy();
  }
}
